// src/stores/TabsStore.ts
// Owns the open tabs and the split tree they live in.
//
// A tab's `path` is its own location, not the browser URL: every pane runs its
// own history, and only the focused pane's location is mirrored into the address
// bar so that reload and shared links still land somewhere sensible.

import { makeAutoObservable } from 'mobx';
import type { ContentAlign, ContentWidth, PaneNode, WorkspaceTab } from '@/features/tabs/types';
import {
  MAX_LIVE_TABS,
  allGroups,
  closeTab,
  createGroup,
  createTab,
  emptyLayout,
  findGroup,
  groupHoldingTab,
  loadLayout,
  moveTab,
  saveLayout,
  setGroupPresentation,
  setRatio,
  splitGroup,
} from '@/features/tabs/logic';

/**
 * Key under which tab-scoped state is filed by a page that is not inside a
 * pane — a harness, a test, anything mounted outside the workspace — and by
 * the workspace itself before the first tab exists.
 */
export const DETACHED_TAB_ID = '__detached__';

class TabsStore {
  tabs: WorkspaceTab[] = [];
  root: PaneNode = createGroup([]);
  focusedGroupId = '';
  pendingNavigation: { tabId: string; path: string } | null = null;
  /** Tab ids most-recently-seen first; decides which panes stay mounted. */
  private recent: string[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    const stored = loadLayout() ?? emptyLayout();
    this.tabs = stored.tabs;
    this.root = stored.root;
    this.focusedGroupId = stored.focusedGroupId;
    this.recent = allGroups(this.root)
      .map((g) => g.activeTabId)
      .filter((id): id is string => id !== null);
  }

  // --- reads -----------------------------------------------------------------

  get groups() {
    return allGroups(this.root);
  }

  get focusedGroup() {
    return findGroup(this.root, this.focusedGroupId) ?? this.groups[0] ?? null;
  }

  get activeTab(): WorkspaceTab | null {
    const id = this.focusedGroup?.activeTabId;
    return id ? this.tabById(id) : null;
  }

  /**
   * The tab the user is working in. Several panes are mounted at once, so
   * anything that speaks for "the current page" — the right rail's entity
   * context and page actions — keys off this rather than off a single global.
   */
  get activeTabId(): string {
    return this.focusedGroup?.activeTabId ?? DETACHED_TAB_ID;
  }

  /** Ids of tabs that still exist; state filed under any other key is stale. */
  get liveTabIds(): string[] {
    return this.tabs.map((tb) => tb.id);
  }

  tabById(id: string): WorkspaceTab | null {
    return this.tabs.find((tb) => tb.id === id) ?? null;
  }

  tabsOf(groupId: string): WorkspaceTab[] {
    const group = findGroup(this.root, groupId);
    if (!group) return [];
    return group.tabIds
      .map((id) => this.tabById(id))
      .filter((tb): tb is WorkspaceTab => tb !== null);
  }

  /**
   * Whether a tab's page should be mounted. Active panes always are; the rest
   * stay alive up to a budget so switching back is instant without letting
   * dozens of pages and their subscriptions pile up.
   */
  isLive(tabId: string): boolean {
    if (this.groups.some((g) => g.activeTabId === tabId)) return true;
    return this.recent.slice(0, MAX_LIVE_TABS).includes(tabId);
  }

  // --- writes ----------------------------------------------------------------

  private persist() {
    saveLayout({
      version: 1,
      tabs: this.tabs,
      root: this.root,
      focusedGroupId: this.focusedGroupId,
    });
  }

  private touch(tabId: string) {
    this.recent = [tabId, ...this.recent.filter((id) => id !== tabId)];
  }

  /** Opens a path in the focused pane, reusing a tab that already shows it. */
  openTab(path: string, title: string, opts: { groupId?: string; activate?: boolean } = {}) {
    const groupId = opts.groupId ?? this.focusedGroupId;
    const group = findGroup(this.root, groupId) ?? this.groups[0];
    if (!group) return;

    const existing = this.tabsOf(group.id).find((tb) => tb.path === path);
    if (existing) {
      this.activateTab(existing.id);
      return;
    }

    const tab = createTab(path, title);
    this.tabs = [...this.tabs, tab];
    this.root = this.replaceGroup({
      ...group,
      tabIds: [...group.tabIds, tab.id],
      activeTabId: opts.activate === false ? group.activeTabId : tab.id,
    });
    this.focusedGroupId = group.id;
    if (opts.activate !== false) this.touch(tab.id);
    this.persist();
  }

  private replaceGroup(next: PaneNode): PaneNode {
    const walk = (node: PaneNode): PaneNode => {
      if (node.id === next.id) return next;
      if (node.kind === 'group') return node;
      return { ...node, children: [walk(node.children[0]), walk(node.children[1])] };
    };
    return walk(this.root);
  }

  activateTab(tabId: string) {
    const group = groupHoldingTab(this.root, tabId);
    if (!group) return;
    this.root = this.replaceGroupById(group.id, { activeTabId: tabId });
    this.focusedGroupId = group.id;
    this.touch(tabId);
    this.persist();
  }

  /** Browser back/forward targets one tab's router, never every pane. */
  navigateTab(tabId: string, path: string) {
    const tab = this.tabById(tabId);
    if (!tab) return;
    this.pendingNavigation = tab.path === path ? null : { tabId, path };
    this.activateTab(tabId);
  }

  completeNavigation(tabId: string, path: string) {
    if (this.pendingNavigation?.tabId === tabId && this.pendingNavigation.path === path) {
      this.pendingNavigation = null;
    }
  }

  private replaceGroupById(groupId: string, patch: Partial<{ activeTabId: string | null }>) {
    const group = findGroup(this.root, groupId);
    if (!group) return this.root;
    const next = { ...group, ...patch };
    const walk = (node: PaneNode): PaneNode => {
      if (node.id === groupId) return next;
      if (node.kind === 'group') return node;
      return { ...node, children: [walk(node.children[0]), walk(node.children[1])] };
    };
    return walk(this.root);
  }

  focusGroup(groupId: string) {
    if (!findGroup(this.root, groupId)) return;
    this.focusedGroupId = groupId;
    this.persist();
  }

  /** Records navigation that happened inside a pane. */
  updateTabLocation(tabId: string, path: string, title: string) {
    const tab = this.tabById(tabId);
    if (!tab || (tab.path === path && tab.title === title)) return;
    this.tabs = this.tabs.map((tb) => (tb.id === tabId ? { ...tb, path, title } : tb));
    this.persist();
  }

  close(tabId: string) {
    if (this.pendingNavigation?.tabId === tabId) this.pendingNavigation = null;
    this.root = closeTab(this.root, tabId);
    this.tabs = this.tabs.filter((tb) => tb.id !== tabId);
    this.recent = this.recent.filter((id) => id !== tabId);
    if (!findGroup(this.root, this.focusedGroupId)) {
      this.focusedGroupId = this.groups[0]?.id ?? '';
    }
    this.persist();
  }

  closeOthers(groupId: string, keepTabId: string) {
    for (const tab of this.tabsOf(groupId)) {
      if (tab.id !== keepTabId && !tab.pinned) this.close(tab.id);
    }
  }

  closeToTheRight(groupId: string, fromTabId: string) {
    const ids = this.tabsOf(groupId).map((tb) => tb.id);
    const at = ids.indexOf(fromTabId);
    if (at < 0) return;
    for (const id of ids.slice(at + 1)) {
      const tab = this.tabById(id);
      if (tab && !tab.pinned) this.close(id);
    }
  }

  togglePinned(tabId: string) {
    this.tabs = this.tabs.map((tb) => (tb.id === tabId ? { ...tb, pinned: !tb.pinned } : tb));
    this.persist();
  }

  reorderOrMove(tabId: string, toGroupId: string, index: number) {
    this.root = moveTab(this.root, tabId, toGroupId, index);
    this.focusedGroupId = toGroupId;
    this.touch(tabId);
    this.persist();
  }

  splitWithTab(groupId: string, tabId: string, direction: 'row' | 'column', before = false) {
    const { root, newGroupId } = splitGroup(this.root, groupId, direction, tabId, before);
    this.root = root;
    this.focusedGroupId = newGroupId;
    this.persist();
  }

  /** Splits a pane and opens a copy of its active tab in the new one. */
  splitGroupById(groupId: string, direction: 'row' | 'column') {
    const group = findGroup(this.root, groupId);
    const active = group?.activeTabId ? this.tabById(group.activeTabId) : null;
    if (!group || !active) return;

    const copy = createTab(active.path, active.title);
    this.tabs = [...this.tabs, copy];
    const withCopy = findGroup(this.root, groupId);
    if (withCopy) {
      this.root = this.replaceGroup({ ...withCopy, tabIds: [...withCopy.tabIds, copy.id] });
    }
    this.splitWithTab(groupId, copy.id, direction);
    this.touch(copy.id);
  }

  setRatio(splitId: string, ratio: number) {
    this.root = setRatio(this.root, splitId, ratio);
    this.persist();
  }

  setPresentation(groupId: string, patch: { width?: ContentWidth; align?: ContentAlign }) {
    this.root = setGroupPresentation(this.root, groupId, patch);
    this.persist();
  }
}

export const tabsStore = new TabsStore();
